<template>
    <div class="oak-tutorial-next-root app-screen-bg" :class="{'oak-tutorial-next-root--fight': step === 'fight', 'oak-tutorial-next-root--skillpoint': step === 'skillpoint'}">
        <div class="oak-tutorial-next-banner">
            <span class="oak-tutorial-next-title-pill">{{ $t('journey.oakNext.title') }}</span>
        </div>
        <div class="oak-tutorial-next-content">
            <template v-if="step === 'loading'">
                <p class="text-center pa-4">{{ $t('journey.oakTutorial.loadingText') }}</p>
            </template>

            <template v-else-if="step === 'learn-attack'">
                <v-card-text class="oak-tutorial-next-text">
                    <p>{{ $t('journey.oakTutorial.learnAttackText') }}</p>
                    <p class="oak-tutorial-next-hint">
                        <strong>{{ $t('journey.oakTutorial.adviceLabel') }}</strong> {{ $t('journey.oakTutorial.learnAttackHintPart1') }}
                        <strong>{{ $t('journey.oakTutorial.diceBase1') }}</strong>{{ $t('journey.oakTutorial.learnAttackHintPart2') }}
                        <strong>{{ $t('journey.oakTutorial.diceBase2') }}</strong> {{ $t('journey.oakTutorial.learnAttackHintPart3') }}
                    </p>
                </v-card-text>
                <PokemonDetail
                    v-if="pokemon"
                    :pokemon="pokemon"
                    :allow-buy-skills="true"
                    :min-height="320"
                    class="oak-tutorial-next-pokemon-detail"
                />
                <v-card-actions class="justify-center oak-tutorial-next-actions">
                    <v-btn
                        rounded
                        large
                        color="primary black--text"
                        class="btn-shadow"
                        :disabled="!hasLearnedSkill"
                        @click="step = 'fight'"
                    >
                        {{ $t('journey.oakTutorial.fightButton') }}
                    </v-btn>
                </v-card-actions>
            </template>

            <template v-else-if="step === 'fight'">
                <ResponsiveDialog
                    v-model="fightIntroOpen"
                    :max-width="360"
                    :full-height="false"
                    :header-title="$t('journey.oakTutorial.fightIntroTitle')"
                    header-class="skin-window-header--oak"
                    card-class="skin-card"
                >
                    <v-card-text class="oak-tutorial-next-popup-text pa-4 text-center">
                        {{ $t('journey.oakTutorial.fightIntroText') }}
                    </v-card-text>
                </ResponsiveDialog>
                <TutorialFakeFight
                    :pokemon="pokemon"
                    :trainer-id="trainerId"
                    class="oak-tutorial-next-fight-fill"
                    @complete="onFightComplete"
                />
            </template>

            <template v-else-if="step === 'skillpoint'">
                <v-card-text class="oak-tutorial-next-text">
                    <p>{{ $t('journey.oakTutorial.skillpointText') }}</p>
                </v-card-text>
                <div v-if="ownTrainer" class="oak-tutorial-next-avatar-col">
                    <v-img v-if="trainerAvatarSrc" :src="trainerAvatarSrc" contain height="100%" class="oak-tutorial-next-avatar-img"/>
                    <v-btn rounded small class="skin-btn-skills oak-tutorial-next-skills-btn" @click="skillsPopupOpen = true">
                        <v-icon v-if="hasAnyBuyableSkill" size="14" color="#FFD700" left>mdi-star</v-icon>
                        {{ $t('journey.oakTutorial.skillsButton') }}
                    </v-btn>
                </div>

                <ResponsiveDialog
                    v-if="ownTrainer"
                    v-model="skillsPopupOpen"
                    :max-width="480"
                    header-icon="⭐"
                    :header-title="$t('journey.oakTutorial.skillsPopupTitle')"
                    header-class="skin-window-header--trainerskills"
                    card-class="skin-card"
                >
                    <v-card-text class="oak-tutorial-next-popup-text skin-window--trainerskills">
                        <p class="oak-tutorial-next-hint">
                            <strong>{{ $t('journey.oakTutorial.adviceLabel') }}</strong> {{ $t('journey.oakTutorial.skillsHint') }}
                        </p>
                        <div class="oak-tutorial-next-skill-list">
                            <div
                                v-for="(skill, key) in trainerSkillsConfig"
                                :key="key"
                                class="oak-tutorial-next-skill-row"
                            >
                                <v-icon size="24" :color="canBuySkill(key) ? '#FFD700' : undefined">{{ skill.icon }}</v-icon>
                                <span class="oak-tutorial-next-skill-name">
                                    {{ $t('trainer.trainerSkills.' + key + '.name') }}
                                    <v-icon v-if="isRecommendedSkill(key)" size="14" color="#FFD700">mdi-star</v-icon>
                                </span>
                                <span class="oak-tutorial-next-skill-value">{{ ownTrainer.trainerSkills[key] }}</span>
                                <v-btn
                                    x-small
                                    rounded
                                    class="skin-btn-buy"
                                    :loading="isBuyingSkill"
                                    :disabled="!canBuySkill(key)"
                                    @click="buySkill(key)"
                                >
                                    {{ $t('journey.oakTutorial.buySkillButton') }}
                                </v-btn>
                            </div>
                        </div>
                    </v-card-text>
                </ResponsiveDialog>

                <v-card-actions class="justify-center oak-tutorial-next-actions">
                    <v-btn
                        rounded
                        large
                        color="primary black--text"
                        class="btn-shadow"
                        :loading="isFinishing"
                        :disabled="!canFinish"
                        @click="finish"
                    >
                        {{ $t('journey.oakTutorial.continueButton') }}
                    </v-btn>
                </v-card-actions>
            </template>
        </div>
    </div>
</template>

<script>
    // 4 interní kroky pro POS_TUTORIAL_OAK, viz claude/components/journey-oak-league.md.
    import {mapActions, mapGetters} from 'vuex';
    import PokemonDetail from '@/components/pokemon/PokemonDetail';
    import TutorialFakeFight from '@/components/journey/next/pieces/TutorialFakeFight';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import {TRAINER_SKILLS} from '@/utils/trainerSkillsEnum';
    import {getTutorialFightStep, clearTutorialFightStep} from '@/utils/tutorialFightStorage';

    // Doporučené startovní skilly - zvýrazněné v seznamu kroku "skillpoint".
    const RECOMMENDED_SKILL_KEYS = ['turistic', 'aim'];

    export default {
        name: 'OakTutorialNext',
        components: {PokemonDetail, TutorialFakeFight, ResponsiveDialog},
        props: {
            position: {type: Object, required: true},
        },
        data() {
            return {
                step: 'loading',
                isFinishing: false,
                fightIntroOpen: false,
                skillsPopupOpen: false,
                trainerSkillsConfig: TRAINER_SKILLS,
                isBuyingSkill: false,
            }
        },
        computed: {
            ...mapGetters({
                activeTeam: 'pokemon/items',
                ownTrainer: 'trainer/ownTrainer',
            }),
            trainerId() {
                return this.position.trainer.id;
            },
            // Musí být computed z Vuex, ne lokální fetch - PokemonDetail.vue's
            // buyNewSkill() patchuje přímo tohle pole, žádný prop zpátky nahoru.
            pokemon() {
                return this.activeTeam.length > 0 ? this.activeTeam[0] : null;
            },
            hasLearnedSkill() {
                return !!(this.pokemon && this.pokemon.skillIds.length > 0);
            },
            canFinish() {
                return !!(this.ownTrainer && this.ownTrainer.experience.skillpoints <= 0);
            },
            hasAnyBuyableSkill() {
                if (!this.ownTrainer) {
                    return false;
                }
                return Object.keys(this.trainerSkillsConfig).some((key) => this.canBuySkill(key));
            },
            trainerAvatarSrc() {
                return this.ownTrainer && this.ownTrainer.img
                    ? require(`@/assets/pic/trainer/${this.ownTrainer.img}`)
                    : null;
            },
        },
        watch: {
            // finishTutorial na chybu jen commitne JOURNEY_SET_ERROR a promise
            // stejně resolvne (viz B4 v claude/roadmap.md) - jediný spolehlivý
            // signál úspěchu je změna position.type z TUTORIAL_OAK pryč.
            'position.type'(newType) {
                if (newType !== 'TUTORIAL_OAK') {
                    clearTutorialFightStep(this.trainerId);
                }
            },
            step(newStep) {
                if (newStep === 'fight') {
                    this.fightIntroOpen = true;
                }
            },
        },
        created() {
            this.loadActiveTeam(this.trainerId).then(() => {
                this.resolveStep();
            });
        },
        methods: {
            ...mapActions({
                loadActiveTeam: 'pokemon/loadActiveTeam',
                loadOwnTrainerAction: 'trainer/loadOwnTrainer',
                buyTrainerSkill: 'trainer/buyTrainerSkill',
                finishTutorial: 'journey/finishTutorial',
            }),
            resolveStep() {
                if (!this.hasLearnedSkill) {
                    this.step = 'learn-attack';
                    return;
                }
                if (getTutorialFightStep(this.trainerId) !== 'complete') {
                    this.step = 'fight';
                    return;
                }
                this.startSkillpointStep();
            },
            onFightComplete() {
                this.startSkillpointStep();
            },
            startSkillpointStep() {
                this.step = 'skillpoint';
                this.loadOwnTrainer();
            },
            isRecommendedSkill(key) {
                return RECOMMENDED_SKILL_KEYS.indexOf(key) !== -1;
            },
            canBuySkill(key) {
                return !!(this.ownTrainer
                    && this.ownTrainer.experience.skillpoints > 0
                    && this.ownTrainer.trainerSkills[key] < this.ownTrainer.experience.level);
            },
            buySkill(key) {
                if (!this.canBuySkill(key) || this.isBuyingSkill) {
                    return;
                }
                this.isBuyingSkill = true;
                this.buyTrainerSkill({trainerId: this.ownTrainer.id, skillName: key}).then((success) => {
                    this.isBuyingSkill = false;
                    if (!success) {
                        this.loadOwnTrainer();
                    }
                });
            },
            loadOwnTrainer() {
                this.loadOwnTrainerAction(this.trainerId);
            },
            finish() {
                this.isFinishing = true;
                this.finishTutorial({positionId: this.position.id}).then(() => {
                    // úspěch pozná watch 'position.type' výše - tady jen odemknout tlačítko pro případný retry
                    this.isFinishing = false;
                });
            },
        },
    }
</script>

<style scoped>
.oak-tutorial-next-root {
    /* žádné vlastní pozadí - používá sdílené .app-screen-bg (_card.sass) jako ostatní next/ obrazovky */
    height: 100%;
    overflow: auto;
    padding-bottom: 54px;
}
.oak-tutorial-next-banner {
    position: relative;
    height: 150px;
    background-image: url('@/assets/pic/web/oak.png');
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 10px;
}
.oak-tutorial-next-title-pill {
    display: inline-block;
    background: rgba(0,0,0,0.65);
    color: #fff;
    font-size: 24px;
    font-weight: 700;
    padding: 6px 24px;
    border-radius: 20px;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
}
.oak-tutorial-next-content {
    padding: 12px;
}
/* krok "fight" vyplňuje celou zbývající výšku obrazovky jako skutečná
   bojová obrazovka - ostatní kroky zůstávají normální scrollovací obsah */
.oak-tutorial-next-root--fight {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.oak-tutorial-next-root--fight .oak-tutorial-next-banner {
    flex: none;
}
.oak-tutorial-next-root--fight .oak-tutorial-next-content {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    /* bez paddingu - BattleLayout.vue si drží vlastní (.battle-layout__row), dvojí padding by zbytečně ořízl omezenou výšku/šířku */
    padding: 0;
    overflow: hidden;
}
.oak-tutorial-next-fight-fill {
    flex: 1 1 auto;
    min-height: 0;
}
/* stejný flex-fill trik jako --fight - avatar dostane veškerou zbývající
   výšku po textu/tlačítkách, jméno trenéra a počet skillpointů se nezobrazují */
.oak-tutorial-next-root--skillpoint {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.oak-tutorial-next-root--skillpoint .oak-tutorial-next-banner {
    flex: none;
}
.oak-tutorial-next-root--skillpoint .oak-tutorial-next-content {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding-bottom: 8px;
    overflow: hidden;
}
.oak-tutorial-next-root--skillpoint .oak-tutorial-next-text {
    flex: none;
}
.oak-tutorial-next-root--skillpoint .oak-tutorial-next-avatar-col {
    flex: 1 1 auto;
    min-height: 0;
}
.oak-tutorial-next-root--skillpoint .oak-tutorial-next-actions {
    flex: none;
}
.oak-tutorial-next-avatar-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 8px;
}
.oak-tutorial-next-avatar-img {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
}
/* rezerva pro plovoucí zavírací křížek (ResponsiveDialog), stejných 69px
   jako ostatní next/ popupy; !important nutné - Vuetify ".v-card__text" má vyšší specificitu */
.oak-tutorial-next-popup-text {
    padding-bottom: var(--responsive-dialog-close-clearance) !important;
}
@media (min-width: 960px) {
    .oak-tutorial-next-popup-text {
        padding-bottom: 16px !important;
    }
}
.oak-tutorial-next-skill-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.oak-tutorial-next-skill-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-radius: 8px;
    background: rgba(255,255,255,0.3);
}
.oak-tutorial-next-skill-name {
    flex: 1 1 auto;
}
.oak-tutorial-next-skill-value {
    font-weight: 700;
    opacity: 0.8;
}
.oak-tutorial-next-pokemon-detail {
    margin-bottom: 8px;
}
.oak-tutorial-next-text {
    font-size: 15px;
}
.oak-tutorial-next-hint {
    font-size: 13px;
    opacity: 0.85;
}
.oak-tutorial-next-actions {
    padding-top: 8px;
}
</style>
